import React, { useEffect, useState } from 'react';
import MediaCollageContainer from 'src/app/components/commonComponents/mediaCollage/MediaCollageContainer';
import ProfileImageView from 'src/app/components/commonComponents/ProfileImageView';
import TagListRenderer from 'src/app/components/commonComponents/TagListRenderer';
import LikeCommentFooter from 'src/app/components/LikeCommentFooter';
import WarningStatusTag from 'src/app/components/commonComponents/statusTags/WarningStatusTag';
import ProfileLayout from 'src/app/layouts/ProfileLayout';
import { ObservationDetailsLoader } from 'src/app/components';
import { useLocation, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/app/services/store';
import { ChildrenStoreData, Observation } from 'src/shared/types/src';
import { canViewAssessmentsPermission, getCurrentChild } from 'src/shared/redux-state/src';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { APP_MESSAGES, ENTITY_TYPE, OBSERVATION_STATUS } from 'src/shared/config/src';
import { fetchObservationDetails } from 'src/shared/services/src';
import { TimelineActions } from 'src/shared/redux-state/src/lib/timeline/TimelineSlice';
import ObservationFrameworks from './ObservationFrameworks';
import TextRenderer from 'src/app/components/commonComponents/TextRenderer';
import MediaTypeDisplay from 'src/app/components/commonComponents/mediaCollage/MediaTypeDisplayComponent';

const ObservationDetailScreen = () => {

	const dispatch = useAppDispatch();
	const location = useLocation();

	const {id}: any = useParams();

	const [observationDetailLoading, setObservationDetailLoading] = useState<boolean>(false);
	const [observationDetails, setObservationDetails] = useState<Observation>();
	const [openLikeDetails, setOpenLikeDetails] = useState<boolean>(false);
	const [openCommentDetails, setOpenCommentDetails] = useState<boolean>(false);

	const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);
	const canViewAssessments: boolean = useAppSelector(canViewAssessmentsPermission);

	useEffect(() => {
		(async () => {
			const { observationDetails, openLikeDetails, openCommentDetails } = location.state ? location.state as Readonly<{ observationDetails: Observation , openLikeDetails?: boolean, openCommentDetails?: boolean }> : {};
			if(openLikeDetails) setOpenLikeDetails(openLikeDetails);
			if(openCommentDetails) setOpenCommentDetails(openCommentDetails);
			if (observationDetails){
				setObservationDetails(observationDetails);
				location.state = {
					observationDetails: null
				}
			}
			else
				await getObservationDetails(id);
		})();
	}, [location.state, location?.state?.observationDetails]);


	const getObservationDetails = async (observationId: string) => {
		try {
			setObservationDetailLoading(true);
			const response = await fetchObservationDetails({ child: currentChild, observationId });
			console.log("response of observation detail: ", response);
			if (response && Object.keys(response).length) {
				setObservationDetails(response.observation);
				// update this observation data in the redux store as well in the timeline data
				await dispatch(TimelineActions.updateObservationDetailsInTimeline({ observation: response.observation }));
			} else {
				setObservationDetails(undefined);
			}
		} catch (error) {
			if (error instanceof AxiosError) {
				const apiError = error as AxiosError;
				if (apiError && apiError.response) {
					const backendErrors = error.response?.data;
					const { errors } = backendErrors;
					toast.error(errors && errors.length ? errors.join(',') : errors.join(", "), { autoClose: 5000 });
				} else {
					toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
				}
			} else {
				toast.error(APP_MESSAGES.generalErrorMessage, { autoClose: 5000 });
			}
		} finally {
			setObservationDetailLoading(false);
		}
	}

	const handleLikeUnlike = async ({ likeCount, likedBySelf } : any) => {
		if(!observationDetails) return;
		const updatedObservationDetails: Observation = { ...observationDetails, likes: { ...observationDetails.likes, count: likeCount, likedByMe: likedBySelf } };
		setObservationDetails(updatedObservationDetails);
		await dispatch(TimelineActions.updateObservationDetailsInTimeline({ observation: updatedObservationDetails }));
	}

	const handleAddComment = async () => {
		if (!observationDetails) return;
		const updatedObservationDetails: Observation = { ...observationDetails, comments: { ...observationDetails.comments, count: observationDetails.comments.count + 1 } };
		setObservationDetails(updatedObservationDetails);
		await dispatch(TimelineActions.updateObservationDetailsInTimeline({ observation: updatedObservationDetails }));
	}

	const handleRemoveComment = async () => {
		if (!observationDetails) return;
		const updatedObservationDetails: Observation = { ...observationDetails, comments: { ...observationDetails.comments, count: observationDetails.comments.count - 1 } };
		setObservationDetails(updatedObservationDetails);
		await dispatch(TimelineActions.updateObservationDetailsInTimeline({ observation: updatedObservationDetails }));
	}

	
  const ObservationItem: React.FC<{ label: string, value: string }> = ({ label, value }) => {
    return (
        <div className='mg-t-20'>
            <div className='fs-12 fs-regular text-black fw-bold text-underline'>{ label }</div>
			<div className='fs-12 text-black mg-t-10 opacity-75'>
				<TextRenderer
					text={value || ""}
				/>
			</div>
        </div>
    )
  }

  return (
		<ProfileLayout title={"Observation Details"} fixedFooter={true} isPrintPage={true} handlePrint={() => window.print()}>
			{observationDetailLoading ? <ObservationDetailsLoader /> : 
			<>
			{observationDetails ? 
				<>
					<div className='d-flex align-items-center'>
						<ProfileImageView
							name={observationDetails.observationBy.name}
							imageUrl={observationDetails.observationBy.photo || ''}
							customContainerStyles='avatar-size-40'
						/>
						<div className='d-flex justify-content-between w-100 mg-l-10 flex-1'>
							<div className='flex-1 fs-14 fw-semibold text-black'>{observationDetails.observationBy.name}</div>
							<div className='fs-12 text-gray-300 white-space-nowrap mg-l-10'>{observationDetails.timestampHuman}</div>
						</div>
					</div>
					<TagListRenderer tags={observationDetails.tags} />

					{ observationDetails.media ?
						<div className="d-flex mx-n-4 media-collage-print-view flex-wrap media-collage-print">
							{observationDetails.media.map((media, index) => {
									return (
										<div className="pd-x-4">
											<div className="media-collage-item">
												<MediaTypeDisplay key={media.id} media={media} collageDisplay={true} />
										</div>
									</div>
									);
								})
							}	
						</div> : null }

					<div className='print-element-hide'><MediaCollageContainer media={observationDetails.media} openCarousel={true}/></div>
					{
						observationDetails?.content ?
							<>
								{observationDetails?.content?.fieldOne?.value ? <ObservationItem label={observationDetails.content.fieldOne.label} value={observationDetails.content.fieldOne.value} /> : null}
								{observationDetails?.content?.fieldTwo?.value ? <ObservationItem label={observationDetails.content.fieldTwo.label} value={observationDetails.content.fieldTwo.value} /> : null}
								{observationDetails?.content?.fieldThree?.value ? <ObservationItem label={observationDetails.content.fieldThree.label} value={observationDetails.content.fieldThree.value} /> : null}
								{observationDetails?.content?.fieldFour?.value ? <ObservationItem label={observationDetails.content.fieldFour.label} value={observationDetails.content.fieldFour.value} /> : null}
							</> : null
					}

					{
						canViewAssessments && (observationDetails?.status !== OBSERVATION_STATUS.PENDING_NURSERY_REVIEW) ?
							<ObservationFrameworks currentChild={currentChild} observationId={observationDetails.id} /> : null
					}

					<div className='like-comment-footer'>
					  {observationDetails.status === OBSERVATION_STATUS.PENDING_NURSERY_REVIEW ?
						  <div className="d-flex align-items-center justify-content-center h-100"><WarningStatusTag statusString={observationDetails.status} /></div> :
						<div className='print-element-hide'><LikeCommentFooter 
						  	likeCount={observationDetails?.likes?.count ?? 0} 
							commentCount={observationDetails?.comments?.count ?? 0}
							likedByMe={observationDetails?.likes?.likedByMe ?? false}
							entityId={observationDetails.id}
							entityType={ENTITY_TYPE.OBSERVATION}
							onLikeUnlike={handleLikeUnlike}
							onAddComment={handleAddComment}
							onDeleteComment={handleRemoveComment}
							canLikeComment={observationDetails?.status === OBSERVATION_STATUS.PUBLISHED}
							openLikeDetails={openLikeDetails}
							openCommentDetails={openCommentDetails}
						/></div>
					  }
					</div>
				</> : null}
				</>
			}
		</ProfileLayout>
  );
};

export default ObservationDetailScreen;
